import { useLingui } from '@lingui/react/macro'
import { useRouter } from '@tanstack/react-router'
import {
  identifyRecentRoutes,
  recentRouteKey,
  recentRouteLabel,
} from 'features/commandPalette/utils/commandPaletteRecents'
import type { CommandPalettePageTarget } from 'features/commandPalette/utils/commandPaletteTargets'
import { getSelectedTab } from 'features/navbar/getSelectedTab'
import { NAV_ITEMS_CONFIG } from 'features/navbar/navConfig'
import { RecentRouteStorage } from 'features/navigation/recentRoutes'
import { useGeckoPermissions } from 'features/permissions'
import { useRolodexEntries } from 'features/rolodex'
import { getEntryTypeMetadata } from 'features/search/config/EntryTypeConfig'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

import { useIsNavItemPermitted } from './useIsNavItemPermitted'

export const RECENT_KIND_LABEL = 'Recent'

/**
 * The pages this user visited most recently on the site, newest first, each
 * reopening its exact recorded route. Re-read from storage on every open
 * (`enabled`): this tab's own visits fire no storage event.
 */
export function useCommandPaletteRecentTargets({
  siteId,
  orgId,
  enabled,
}: {
  siteId: string | undefined
  orgId: string | undefined
  enabled: boolean
}): CommandPalettePageTarget[] {
  const { i18n } = useLingui()
  const router = useRouter()
  const { user } = useGeckoPermissions()
  const isPermitted = useIsNavItemPermitted({ siteId, orgId })
  const userId = user?.id

  const recents = useMemo(
    () =>
      identifyRecentRoutes(
        enabled && userId && siteId
          ? RecentRouteStorage.getRecentRoutes(userId, siteId)
          : [],
        getSelectedTab
      ),
    [enabled, siteId, userId]
  )
  const unitIds = useMemo(
    () =>
      Array.from(
        new Set(
          recents.flatMap(({ identity }) =>
            identity.unitId ? [identity.unitId] : []
          )
        )
      ),
    [recents]
  )
  const { units } = useRolodexEntries(
    unitIds.length > 0
      ? { type: RolodexEntryType.UNIT, ids: unitIds, limit: unitIds.length }
      : undefined,
    { allowCache: true }
  )

  return useMemo(() => {
    const unitNameById = new Map(units.map((unit) => [unit.id, unit.data.name]))
    const unitIcon = getEntryTypeMetadata(RolodexEntryType.UNIT).icon

    return recents.flatMap(({ route, identity }) => {
      const config = NAV_ITEMS_CONFIG[identity.destination]
      if (!config || !isPermitted(identity.destination)) return []
      const label = recentRouteLabel(
        identity,
        identity.unitId ? unitNameById.get(identity.unitId) : undefined,
        i18n._(config.label)
      )
      if (!label) return []

      return [
        {
          id: `recent:${recentRouteKey(identity)}`,
          kind: 'page' as const,
          label,
          kindLabel: RECENT_KIND_LABEL,
          icon: identity.unitId ? unitIcon : config.icon,
          destination: identity.destination,
          unitId: identity.unitId,
          href: router.buildLocation({
            to: route.pathname,
            search: route.search ?? {},
          }).href,
        },
      ]
    })
  }, [i18n, isPermitted, recents, router, units])
}
